import { Shapefile } from '../../app/feathers/districts/Shapefile';

export type Transform =
	| { type: 'trim' }
	| { type: 'lowercase' }
	| { type: 'uppercase' }
	| { type: 'pretty_case' }
	| { type: 'remove_spaces' }
	| { type: 'remove_punctuation' }
	| { type: 'to_number' }
	| { type: 'replace'; find: string; replacement: string; caseSensitive?: boolean }
	| { type: 'regex_replace'; pattern: string; replacement: string; flags?: string }
	| { type: 'extract_regex'; pattern: string; flags?: string }
	| { type: 'split'; delimiter: string; take: 'first' | 'last' | number }
	| { type: 'strip_prefix'; value: string; caseSensitive?: boolean }
	| { type: 'strip_suffix'; value: string; caseSensitive?: boolean };

export interface ColumnTransformDefinition {
	source: string;
	target: string;
	transforms: Transform[];
}

export interface TransformPreviewSample {
	rowIndex: number;
	originalValue: string;
	finalValue: string;
}

function toSafeString(value: unknown): string {
	return String(value ?? '');
}

function escapeRegexValue(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function createSeededRandom(seed: number): () => number {
	let nextSeed = seed >>> 0;
	return () => {
		nextSeed += 0x6d2b79f5;
		let value = Math.imul(nextSeed ^ (nextSeed >>> 15), 1 | nextSeed);
		value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
		return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
	};
}

function applyCaseInsensitiveStrip(value: string, stripValue: string, fromStart: boolean): string {
	if (!stripValue) return value;
	const valueLower = value.toLowerCase();
	const stripLower = stripValue.toLowerCase();
	if (fromStart && valueLower.startsWith(stripLower)) {
		return value.slice(stripValue.length);
	}
	if (!fromStart && valueLower.endsWith(stripLower)) {
		return value.slice(0, value.length - stripValue.length);
	}
	return value;
}

function toPrettyCase(value: string): string {
	const titleCased = value
		.toLowerCase()
		.replaceAll(/\b([a-z])/g, (match) => match.toUpperCase());

	return titleCased.replaceAll(/\bMc([a-z])/g, (_, letter: string) => {
		return `Mc${letter.toUpperCase()}`;
	});
}

export function applySingleTransform(value: unknown, transform: Transform): string {
	const currentValue = toSafeString(value);
	switch (transform.type) {
		case 'trim':
			return currentValue.trim();
		case 'lowercase':
			return currentValue.toLowerCase();
		case 'uppercase':
			return currentValue.toUpperCase();
		case 'pretty_case':
			return toPrettyCase(currentValue);
		case 'remove_spaces':
			return currentValue.replaceAll(/\s+/g, '');
		case 'remove_punctuation':
			return currentValue.replaceAll(/[^\w\s]/g, '');
		case 'to_number':
			return String(Number(currentValue));
		case 'replace': {
			if (!transform.find) return currentValue;
			if (transform.caseSensitive !== false) {
				return currentValue.replaceAll(transform.find, transform.replacement);
			}
			return currentValue.replaceAll(
				new RegExp(escapeRegexValue(transform.find), 'gi'),
				transform.replacement
			);
		}
		case 'regex_replace': {
			try {
				return currentValue.replaceAll(
					new RegExp(transform.pattern, transform.flags || 'g'),
					transform.replacement
				);
			} catch (error) {
				return currentValue;
			}
		}
		case 'extract_regex': {
			try {
				const regex = new RegExp(transform.pattern, transform.flags || '');
				const match = currentValue.match(regex);
				if (!match) return '';
				return match.length > 1 ? (match[1] || '') : (match[0] || '');
			} catch (error) {
				return currentValue;
			}
		}
		case 'split': {
			const splitValue = currentValue.split(transform.delimiter);
			if (transform.take === 'first') {
				return splitValue[0] || '';
			}
			if (transform.take === 'last') {
				return splitValue[splitValue.length - 1] || '';
			}
			const index = transform.take;
			return splitValue[index] || '';
		}
		case 'strip_prefix': {
			if (!transform.value) return currentValue;
			if (transform.caseSensitive === false) {
				return applyCaseInsensitiveStrip(currentValue, transform.value, true);
			}
			return currentValue.startsWith(transform.value)
				? currentValue.slice(transform.value.length)
				: currentValue;
		}
		case 'strip_suffix': {
			if (!transform.value) return currentValue;
			if (transform.caseSensitive === false) {
				return applyCaseInsensitiveStrip(currentValue, transform.value, false);
			}
			return currentValue.endsWith(transform.value)
				? currentValue.slice(0, currentValue.length - transform.value.length)
				: currentValue;
		}
		default:
			return currentValue;
	}
}

export function applyTransformChain(value: unknown, transforms: Transform[]): string {
	return transforms.reduce((currentValue, transform) => {
		return applySingleTransform(currentValue, transform);
	}, toSafeString(value));
}

export function applyColumnTransformsToRow(
	row: Shapefile,
	columnTransforms: ColumnTransformDefinition[]
): Shapefile {
	const nextRow = { ...row };
	columnTransforms.forEach(({ source, target, transforms }) => {
		nextRow[target] = applyTransformChain(nextRow[source], transforms);
	});
	return nextRow;
}

export function createUniqueColumnName(baseName: string, existingHeaders: string[]): string {
	const cleanedBaseName = baseName.trim() || 'transformed_column';
	const existing = new Set(existingHeaders);
	if (!existing.has(cleanedBaseName)) {
		return cleanedBaseName;
	}
	let suffix = 2;
	let candidate = `${cleanedBaseName}_${suffix}`;
	while (existing.has(candidate)) {
		suffix += 1;
		candidate = `${cleanedBaseName}_${suffix}`;
	}
	return candidate;
}

export function getRandomSampleIndexes(total: number, sampleSize: number, seed: number): number[] {
	if (total <= 0 || sampleSize <= 0) return [];
	const random = createSeededRandom(seed);
	const allIndexes = Array.from({ length: total }, (_, index) => index);
	for (let index = allIndexes.length - 1; index > 0; index -= 1) {
		const swapIndex = Math.floor(random() * (index + 1));
		const temp = allIndexes[index];
		allIndexes[index] = allIndexes[swapIndex];
		allIndexes[swapIndex] = temp;
	}
	return allIndexes.slice(0, Math.min(sampleSize, total)).sort((a, b) => a - b);
}

export function buildTransformPreviewSamples(
	rows: Shapefile[],
	sourceColumn: string,
	transforms: Transform[],
	sampleIndexes: number[]
): TransformPreviewSample[] {
	return sampleIndexes.map((rowIndex) => {
		const row = rows[rowIndex];
		const originalValue = toSafeString(row?.[sourceColumn]);
		return {
			rowIndex,
			originalValue,
			finalValue: applyTransformChain(originalValue, transforms),
		};
	});
}
